import { Grid, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { useCurrentSensorConfiguration } from '@/api/device';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { headerBgLightBlue } from '@/theme';
import { composeSensorInfo, getFirmwareVersion } from '@/utils';

const SENSOR_DOCS_BY_NAME: Record<string, string> = {
    bm_aanderaa_currents_1:
        'https://sofarocean.notion.site/Aanderaa-4830-Doppler-Current-Sensor-3448ff95945081c5b438c793df697b47',
    bm_aanderaa_salinity_1:
        'https://sofarocean.notion.site/Aanderaa-5990B-Conductivity-Sensor-3448ff95945081779388ec7622608ea6',
    bm_borealis_1: 'https://sofarocean.notion.site/AOS-BOREALIS-3448ff959450815e8b6cc7f9e42c8327',
    bm_borealis_2: 'https://sofarocean.notion.site/AOS-BOREALIS-3448ff959450815e8b6cc7f9e42c8327',
    bm_pme_do_sensor_1: 'https://sofarocean.notion.site/PME-MicroDOT-3448ff95945081e0b14dfb0066203b3a',
    bm_rbr_coda_3_D: 'https://sofarocean.notion.site/RBRcoda-D-3448ff95945081a6b113c615a41fdeca',
    bm_rbr_coda_3_T: 'https://sofarocean.notion.site/RBRcoda-T-3448ff95945081d2b992c598d22fdc9e',
    bm_rbr_coda_3_TD: 'https://sofarocean.notion.site/RBRcoda-T-D-3448ff9594508181bb88ea3a1d873741',
    bm_seapoint_turbidity_1:
        'https://sofarocean.notion.site/Seapoint-Turbidity-Meter-STM-3448ff95945081ce8c0ed2d3dff4b29d',
    bm_soft_temperature_1: 'https://sofarocean.notion.site/Sofar-Temperature-3448ff95945081a192dae35f6b0b0c73',
    rbrcoda3_depth: 'https://sofarocean.notion.site/RBRcoda-D-3448ff95945081a6b113c615a41fdeca',
    rbrcoda3_module1: 'https://sofarocean.notion.site/RBRcoda-D-3448ff95945081a6b113c615a41fdeca',
    seapoint_stms_1: 'https://sofarocean.notion.site/Seapoint-Turbidity-Meter-STM-3448ff95945081ce8c0ed2d3dff4b29d',
    sofar_tsys01: 'https://sofarocean.notion.site/Sofar-Temperature-3448ff95945081a192dae35f6b0b0c73',
    sofar_tsys01_2: 'https://sofarocean.notion.site/Sofar-Temperature-3448ff95945081a192dae35f6b0b0c73',
};

export const SensorConfigurationSection = () => {
    const { data: sensorConfig, isPending: isSensorConfigPending, isFetching } = useCurrentSensorConfiguration();

    if (isSensorConfigPending) {
        return (
            <Grid container gap={2}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h4">Subsurface sensors</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <GenericSkeleton />
                </Grid>
            </Grid>
        );
    }

    if (!sensorConfig || isEmpty(sensorConfig)) {
        return null;
    }

    const sensorInfo = composeSensorInfo(sensorConfig);
    const hasChainedSensors = !isEmpty(sensorInfo.chainedSensors);
    const { bristlemouth } = sensorInfo;

    return (
        <>
            <Stack gap={2}>
                <SectionHeader title="Subsurface data transmission" isLoading={isFetching} />
                <Grid container spacing={1} size={{ xs: 12, sm: 8 }} sx={{ mx: 5 }} justifyContent="space-between">
                    <Grid size={{ xs: 12 }} display="flex" justifyContent="space-between" gap={2}>
                        <Typography>Samples per message</Typography>
                        <Typography>{sensorInfo.mainSensor.samplesPerMessage ?? <em>Unknown</em>}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} display="flex" justifyContent="space-between" gap={2}>
                        <Typography>Sample interval</Typography>
                        <Typography>{sensorInfo.mainSensor.sampleIntervalMinutes} minutes</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} display="flex" justifyContent="space-between" gap={2}>
                        <Typography>Sample duration</Typography>
                        <Typography>{sensorInfo.mainSensor.sampleDurationMinutes} minutes</Typography>
                    </Grid>
                    {bristlemouth && (
                        <Grid size={{ xs: 12 }} display="flex" justifyContent="space-between" gap={2}>
                            <Typography>Spotter Bristlemouth version</Typography>
                            <Typography>{getFirmwareVersion(sensorInfo.mainSensor) ?? <em>Unknown</em>}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Stack>
            <Stack gap={2}>
                <SectionHeader title="Connected subsurface sensors" isLoading={isFetching} />
                {!hasChainedSensors ? (
                    <Typography sx={{ mx: 5 }}>
                        <em>No connected sensors</em>
                    </Typography>
                ) : (
                    <Table size="small" border={1} sx={{ width: 'initial', borderColor: 'divider', mx: 5 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: headerBgLightBlue }}>
                                <TableCell>Sensor position</TableCell>
                                <TableCell>Sensor name</TableCell>
                                <TableCell>Sensor output label</TableCell>
                                {bristlemouth && <TableCell>Firmware version</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sensorInfo.chainedSensors.map((sensor) => (
                                <TableRow key={`${sensor.position}-${sensor.displayName}`}>
                                    <TableCell>Sensor {sensor.position}</TableCell>
                                    <TableCell>
                                        {sensor.name in SENSOR_DOCS_BY_NAME ? (
                                            <Link
                                                component="a"
                                                underline="hover"
                                                href={SENSOR_DOCS_BY_NAME[sensor.name]}
                                                target="_blank"
                                            >
                                                {sensor.displayName}
                                            </Link>
                                        ) : (
                                            <>{sensor.displayName}</>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="text.secondary">{sensor.name}</Typography>
                                    </TableCell>
                                    {bristlemouth && (
                                        <TableCell>{getFirmwareVersion(sensor) ?? <em>Unknown</em>}</TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Stack>
        </>
    );
};
